<script>
import DropdownSelect from "@/components/DropdownSelect.vue";
import FormsDateTimePicker from "@/components/Forms/DateTimePicker.vue";
import RelationInput from "@/components/Forms/RelationInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import MarkdownEditor from "@/components/shared/MarkdownEditor.vue";
import UserComment from "@/components/shared/UserComment.vue";
import UserBlurb from "@/components/User/UserBlurb.vue";
import { capitalize } from "@/helpers/filters";
import { onVisible } from "@/helpers/onVisible";
import { isAdminOfCommunity } from "@/helpers/permissions/communities";
import { isCoownerOrOwner } from "@/helpers/permissions/loanables";
import { isGlobalAdmin } from "@/helpers/permissions/users";
import { del, post, put } from "@/requests/server";
import dayjs from "dayjs";
import Vue from "vue";

export default Vue.extend({
  name: "IncidentForm",
  components: {
    MarkdownEditor,
    DropdownSelect,
    FormsDateTimePicker,
    UserBlurb,
    RelationInput,
    IconButton,
    UserComment,
  },
  props: {
    incident: {
      type: Object,
      required: true,
    },
    loanable: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      changingAssignee: false,
      changingBlockingUntil: false,
      blockingUntil: this.incident.blocking_until,
      showIncidentDetailsToBlockedBorrowers: this.incident.show_details_to_blocked_borrowers,
      notificationOptions: [
        {
          value: "all",
          label: this.$t("incidents.notification_subscription_options.all"),
        },
        {
          value: "resolved_only",
          label: this.$t("incidents.notification_subscription_options.resolved_only"),
        },
        {
          value: "none",
          label: this.$t("incidents.notification_subscription_options.none"),
        },
      ],
    };
  },
  computed: {
    canUpdateIncident() {
      const user = this.$store.state.user;

      return (
        this.incident.assignee?.id === user.id ||
        isCoownerOrOwner(user, this.loanable) ||
        isAdminOfCommunity(user, { id: this.incident.loan_community_id }) ||
        isGlobalAdmin(user)
      );
    },
    canAddNote() {
      return (
        this.canUpdateIncident || this.incident.reported_by_user?.id === this.$store.state.user.id
      );
    },
    isAdmin() {
      return isGlobalAdmin(this.$store.state.user);
    },
    user() {
      return this.$store.state.user;
    },
  },
  mounted() {
    if (this.$refs.notes) {
      onVisible(this.$refs.notes, this.scrollToLastComment);
    }
  },
  methods: {
    update(incident) {
      incident.updated_at = dayjs().toISOString();
      this.blockingUntil = incident.blocking_until;
      this.showIncidentDetailsToBlockedBorrowers = incident.show_details_to_blocked_borrowers;
      this.$emit("update", incident);
    },
    scrollToLastComment() {
      this.$nextTick(() => {
        const noteDiv = this.$refs.notes;
        noteDiv.scrollTop = noteDiv.scrollHeight;
      });
    },
    async setAssignee(assignee) {
      await put(
        `/incidents/${this.incident.id}/assignee`,
        {
          assignee_id: assignee ? assignee.id : null,
        },
        {
          notifications: {
            action: this.$t("incidents.update_notifications.assignee.action"),
            onSuccess: this.$t("incidents.update_notifications.assignee.success"),
          },
        }
      );
      this.update({
        ...this.incident,
        assignee_id: assignee ? assignee.id : null,
        assignee,
      });
      this.changingAssignee = false;
    },
    async setBlockingUntil() {
      const { data } = await put(
        `/incidents/${this.incident.id}/block`,
        {
          blocking_until: this.blockingUntil,
          show_details_to_blocked_borrowers: this.showIncidentDetailsToBlockedBorrowers,
        },
        {
          notifications: {
            action: this.$t("incidents.update_notifications.update.action"),
            onSuccess: this.$t("incidents.update_notifications.update.success"),
          },
        }
      );
      this.update({
        ...this.incident,
        ...data,
      });
      this.changingBlockingUntil = false;
    },
    async resolve() {
      const { data } = await put(`/incidents/${this.incident.id}/complete`, this.action, {
        notifications: {
          action: this.$t("incidents.update_notifications.resolve.action"),
          onSuccess: this.$t("incidents.update_notifications.resolve.success"),
        },
      });
      this.update({
        ...this.incident,
        ...data,
        resolved_by_user: this.$store.state.user,
      });
    },
    async reopen() {
      const { data } = await put(
        `/incidents/${this.incident.id}/reopen`,
        {},
        {
          notifications: {
            action: this.$t("incidents.update_notifications.reopen.action"),
            onSuccess: this.$t("incidents.update_notifications.reopen.success"),
          },
        }
      );
      this.update({
        ...this.incident,
        ...data,
      });
    },
    async addNote(text) {
      const { data } = await post(
        `/incidents/${this.incident.id}/note`,
        {
          text,
        },
        {
          notifications: {
            action: this.$t("incidents.update_notifications.add_note.action"),
          },
        }
      );
      this.update({
        ...this.incident,
        notes: [
          ...this.incident.notes,
          {
            ...data,
            author: this.$store.state.user,
          },
        ],
      });
      this.scrollToLastComment();
    },
    async updateDescription(text) {
      const { data } = await put(
        `/incidents/${this.incident.id}`,
        {
          comments_on_incident: text,
        },
        {
          notifications: {
            action: this.$t("incidents.update_notifications.update_description.action"),
          },
        }
      );

      this.update({
        ...this.incident,
        ...data,
      });
    },
    async updateNote(note, text) {
      const { data } = await put(
        `/incidents/${this.incident.id}/note/${note.id}`,
        {
          text,
        },
        {
          notifications: {
            action: this.$t("incidents.update_notifications.update_update.action"),
          },
        }
      );

      const notes = this.incident.notes.map((n) => {
        if (n.id === note.id) {
          return data;
        }
        return n;
      });

      this.update({
        ...this.incident,
        notes,
      });
    },
    async deleteNote(note) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        this.$t("components.incident.incident_form.delete_note.confirm_title"),
        {
          size: "sm",
          buttonSize: "sm",
          okTitle: this.$t("components.incident.incident_form.delete_note.confirm_ok"),
          cancelTitle: capitalize(this.$t("forms.cancel")),
          okVariant: "danger",
          cancelVariant: "ghost-secondary",
          footerClass: "p-2 border-top-0",
          centered: true,
        }
      );

      if (!confirm) {
        return;
      }

      await del(`/incidents/${this.incident.id}/note/${note.id}`, {
        notifications: {
          action: this.$t("incidents.update_notifications.delete_update.action"),
        },
      });

      const notes = this.incident.notes.filter((n) => n.id !== note.id);
      this.update({
        ...this.incident,
        notes,
      });
    },
    async updateNotificationSubscription(level) {
      await put(
        `/incidents/${this.incident.id}/subscription`,
        { level },
        {
          notifications: {
            action: this.$t("incidents.update_notifications.update_notifications.action"),
          },
        }
      );
      this.incident.notification_subscription = level;
    },
    dayIsInThePast(day) {
      return dayjs().startOf("day").isAfter(day, "day");
    },
    dateIsInThePast(date) {
      return dayjs().isAfter(date, "minute");
    },
  },
});
</script>

<template>
  <div>
    <b-row>
      <b-col cols="12" class="col-800-6">
        <dl>
          <dt>{{ $t("components.incident.incident_form.description") }}</dt>
          <dd>
            <user-comment
              :text="incident.comments_on_incident"
              :user="incident.reported_by_user"
              :date="incident.created_at"
              :update="
                isAdmin || (incident.reported_by_user && incident.reported_by_user.id === user.id)
                  ? updateDescription
                  : null
              "
            ></user-comment>
          </dd>
        </dl>
      </b-col>

      <b-col cols="12" class="col-800-6">
        <dl>
          <template v-if="incident.status === 'in_process'">
            <template v-if="incident.blocking_until || changingBlockingUntil">
              <dt>{{ $t("components.incident.incident_form.blocking.title") }}</dt>
              <dd v-if="changingBlockingUntil && canUpdateIncident">
                <forms-date-time-picker
                  v-model="blockingUntil"
                  clearable
                  :disabled-dates-fct="dayIsInThePast"
                  :disabled-times-fct="dateIsInThePast"
                />
                <b-checkbox
                  v-if="blockingUntil"
                  v-model="showIncidentDetailsToBlockedBorrowers"
                  class="mt-2 mb-2"
                  switch
                >
                  {{ $t("components.incident.incident_form.blocking.show_details") }}
                </b-checkbox>
                <b-alert
                  v-if="showIncidentDetailsToBlockedBorrowers"
                  variant="warning"
                  show
                  class="p-2"
                >
                  <strong
                    >{{
                      $t("components.incident.incident_form.blocking.warning.title")
                    }}&nbsp;:</strong
                  >
                  {{ $t("components.incident.incident_form.blocking.warning.message") }}
                </b-alert>
                <div class="mt-1">
                  <icon-button size="sm" role="save" :onclick="setBlockingUntil">
                    {{ $t("forms.save") | capitalize }}
                  </icon-button>
                  <icon-button
                    class="ml-1"
                    size="sm"
                    role="cancel"
                    :onclick="
                      () => {
                        changingBlockingUntil = false;
                        blockingUntil = incident.blocking_until;
                        showIncidentDetailsToBlockedBorrowers =
                          incident.show_details_to_blocked_borrowers;
                      }
                    "
                  >
                    {{ $t("forms.cancel") | capitalize }}
                  </icon-button>
                </div>
              </dd>
              <dd v-else-if="incident.blocking_until">
                <div class="d-flex flex-row align-items-start justify-content-between">
                  <div>
                    <div>
                      {{ incident.blocking_until | date }}
                      {{ incident.blocking_until | time }}
                    </div>

                    <b-badge
                      v-if="incident.show_details_to_blocked_borrowers && canUpdateIncident"
                      variant="warning"
                    >
                      {{ $t("components.incident.incident_form.borrower_visible_details") }}
                    </b-badge>
                  </div>

                  <icon-button
                    v-if="canUpdateIncident"
                    class="ml-2"
                    size="sm"
                    role="edit"
                    @click="changingBlockingUntil = true"
                  />
                </div>
              </dd>
            </template>

            <icon-button
              v-else-if="canUpdateIncident"
              size="sm"
              variant="outline-warning"
              icon="exclamation-triangle"
              class="mb-2"
              @click="changingBlockingUntil = true"
            >
              {{ $t("components.incident.incident_form.block_loans") }}
            </icon-button>
          </template>
          <template v-if="incident.resolved_by_user">
            <dt>
              {{ $t("components.incident.incident_form.resolved_by") }}
            </dt>
            <dd>
              <user-blurb :user="incident.resolved_by_user" :show-admin-link="isAdmin" />
            </dd>
          </template>
          <dt>
            {{ $t("components.incident.incident_form.responsible") }}
          </dt>

          <dd v-if="!canUpdateIncident || !changingAssignee">
            <user-blurb
              v-if="incident.assignee"
              :user="incident.assignee"
              :show-admin-link="isAdmin"
            >
              <template #buttons>
                <div
                  v-if="canUpdateIncident && incident.status === 'in_process'"
                  class="d-flex align-items-center"
                >
                  <icon-button size="sm" role="edit" @click="changingAssignee = true">
                  </icon-button>
                  <icon-button class="ml-1" size="sm" role="remove-item" :onclick="setAssignee">
                  </icon-button>
                </div>
              </template>
            </user-blurb>
            <icon-button
              v-else-if="canUpdateIncident"
              role="add"
              size="sm"
              @click="changingAssignee = true"
            >
              {{ $t("components.incident.incident_form.assignee.assign") | capitalize }}
            </icon-button>
            <div v-else class="font-italic">
              {{ $t("components.incident.incident_form.assignee.none") }}
            </div>
          </dd>
          <dd v-else>
            <relation-input
              :query="{
                value: 'id',
                text: 'full_name',
                slug: 'users',
                params: {
                  relations: 'avatar',
                  assignableToIncident: incident.id,
                },
              }"
              :min-search-length="1"
              :value="incident.assignee_id"
              :object-value="incident.assignee"
              name="assignee_id"
              @input="setAssignee"
            />

            <icon-button
              v-if="canUpdateIncident"
              class="mt-1"
              size="sm"
              role="cancel"
              @click="changingAssignee = false"
            >
              {{ $t("forms.cancel") | capitalize }}
            </icon-button>
          </dd>
        </dl>
      </b-col>
    </b-row>
    <dl class="mb-0">
      <dt>{{ $t("components.incident.incident_form.updates.title") }}</dt>
      <dd class="mb-0">
        <template v-if="incident.notes && incident.notes.length > 0">
          <div ref="notes" class="incident-notes">
            <user-comment
              v-for="note in incident.notes"
              :key="note.id"
              :text="note.text"
              :user="note.author"
              :date="note.created_at"
              :update="
                isAdmin || note.author.id === user.id ? (text) => updateNote(note, text) : null
              "
              :destroy="async () => deleteNote(note)"
              :can-destroy="isAdmin || note.author.id === user.id"
              :updated-date="note.updated_at"
            />
          </div>
        </template>
        <div v-else class="font-italic">
          {{ $t("components.incident.incident_form.updates.none") }}
        </div>
        <markdown-editor
          :save="addNote"
          :placeholder="$t('components.incident.incident_form.updates.placeholder')"
        />
        <span v-if="incident.show_details_to_blocked_borrowers" class="small">
          <strong
            >{{ $t("components.incident.incident_form.updates.warning.title") }}&nbsp;:</strong
          >
          {{ $t("components.incident.incident_form.updates.warning.message") }}
        </span>
      </dd>
    </dl>

    <div class="mt-3 button-list justify-content-between">
      <template v-if="canUpdateIncident">
        <icon-button
          v-if="incident.status === 'in_process'"
          size="sm"
          variant="success"
          icon="check2-circle"
          :onclick="resolve"
        >
          {{ $t("components.incident.incident_form.buttons.resolve") }}
        </icon-button>
        <icon-button
          v-if="incident.status === 'completed'"
          size="sm"
          variant="outline-danger"
          icon="x-octagon"
          :onclick="reopen"
        >
          {{ $t("components.incident.incident_form.buttons.reopen") }}
        </icon-button>
      </template>

      <dropdown-select
        :icon="incident.notification_subscription === 'none' ? 'bell-slash' : 'bell'"
        :label="$t('components.incident.incident_form.notifications.title')"
        size="sm"
        :value="incident.notification_subscription"
        :options="notificationOptions"
        :onselect="updateNotificationSubscription"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.incident-notes {
  max-height: 20rem;
  overflow: auto;
  margin-bottom: 1rem;
}

dt {
  margin-bottom: 0.5rem;
}
.row {
  container-type: inline-size;
  // Only split into two column when the row is larger than 800px
  @container (width > 800px) {
    .col-800-6 {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }
}
</style>
